import { useState, useEffect, useMemo } from "react";
import {
  ArrowLeft,
  Building2,
  Award,
  UserCheck,
  Users,
  Shield,
  Briefcase,
  FileText,
  Settings,
  Store,
  Sparkles,
  MessageSquare,
  QrCode,
  Truck,
  Sliders,
  ChevronDown,
  FlaskConical,
  LifeBuoy,
} from "lucide-react";
import SidebarSection from "./SidebarSection";
import styles from "./Sidebar.module.css";

const TAB_ICONS = {
  Building2,
  Award,
  UserCheck,
  Users,
  Shield,
  Briefcase,
  FileText,
  Settings,
  Store,
  Sparkles,
  MessageSquare,
  QrCode,
  Truck,
  Sliders,
  FlaskConical,
  LifeBuoy,
};

// Logical grouping rules for property tabs
const PROPERTY_GROUP_DEFINITIONS = [
  {
    groupId: "access",
    category: "Gate & Access Control",
    icon: Shield,
    tabIds: ["gates", "visits", "network", "industrial-settings"],
  },
  {
    groupId: "personnel",
    category: "Personnel & Approvals",
    icon: Users,
    tabIds: ["leadership", "approvals", "directories", "staff", "testers"],
  },
  {
    groupId: "engagement",
    category: "Community & Helpdesk",
    icon: MessageSquare,
    tabIds: ["chats", "support"],
  },
  {
    groupId: "compliance",
    category: "Compliance & Settings",
    icon: FileText,
    tabIds: ["documents", "settings"],
  },
];

const PropertyNavDropdown = ({
  group,
  activeTab,
  isCollapsed,
  onSelectTab,
}) => {
  const isAnyChildActive = group.tabs.some((t) => t.id === activeTab);
  
  // Initialize open state: if any child is active, open; otherwise check localStorage
  const [isOpen, setIsOpen] = useState(() => {
    if (isAnyChildActive) return true;
    try {
      const saved = localStorage.getItem(`sidebar_group_${group.groupId}`);
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Expand automatically when activeTab changes to a child in this group
  useEffect(() => {
    if (isAnyChildActive) {
      setIsOpen(true);
    }
  }, [activeTab]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(`sidebar_group_${group.groupId}`, JSON.stringify(next));
      } catch (err) {
        // Ignore localStorage quota or access errors in private browsing
        console.debug("Failed to save sidebar state:", err);
      }
      return next;
    });
  };

  const IconComponent = group.icon || Shield;
  const totalBadge = group.tabs.reduce(
    (sum, t) => sum + (typeof t.badge === "number" && t.badge > 0 ? t.badge : 0),
    0
  );

  return (
    <div className={styles.dropdownGroup}>
      <button
        type="button"
        onClick={handleToggle}
        className={`${styles.dropdownTrigger} ${
          isAnyChildActive ? styles.dropdownTriggerActive : ""
        } ${isCollapsed ? styles.navItemCollapsed : ""}`}
        title={isCollapsed ? group.category : undefined}
      >
        {IconComponent && <IconComponent className={styles.itemIcon} />}
        {!isCollapsed && (
          <>
            <span className={styles.itemLabel}>{group.category}</span>
            {totalBadge > 0 && !isOpen && (
              <span className={styles.externalBadge}>{totalBadge}</span>
            )}
            <ChevronDown
              size={16}
              className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
            />
          </>
        )}
      </button>

      {isOpen && !isCollapsed && (
        <div className={styles.dropdownContent}>
          {group.tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const TabIcon = TAB_ICONS[tab.icon] || Building2;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onSelectTab(tab.id)}
                className={`${styles.navItem} ${
                  isActive ? styles.navItemActive : ""
                }`}
                title={tab.label}
              >
                <TabIcon className={styles.itemIcon} />
                <span className={styles.itemLabel}>{tab.label}</span>
                {tab.badge && Number(tab.badge) > 0 ? (
                  <span className={styles.externalBadge}>{tab.badge}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SidebarPropertyNav = ({
  propertyContext,
  isCollapsed,
  onCloseMobile,
}) => {
  if (!propertyContext || !propertyContext.property) return null;

  const { property, activeTab, onSelectTab, onBack, tabs = [] } = propertyContext;

  const handleSelect = (tabId) => {
    if (onSelectTab) onSelectTab(tabId);
    if (onCloseMobile) onCloseMobile();
  };

  const handleBack = () => {
    if (onBack) onBack();
    if (onCloseMobile) onCloseMobile();
  };

  // Group tabs into standalone items and categorized dropdowns
  const { topTabs, groupedSections, otherTabs } = useMemo(() => {
    // 1. Primary architecture overview tab (e.g., "towers" / "Buildings & Depts")
    const top = tabs.filter((t) => t.id === "towers");

    // 2. Map tabs to matching groups
    const grouped = [];
    const claimedTabIds = new Set(top.map((t) => t.id));

    PROPERTY_GROUP_DEFINITIONS.forEach((def) => {
      const matchingTabs = tabs.filter(
        (t) => def.tabIds.includes(t.id) && !claimedTabIds.has(t.id)
      );
      if (matchingTabs.length > 0) {
        matchingTabs.forEach((t) => claimedTabIds.add(t.id));
        grouped.push({
          ...def,
          tabs: matchingTabs,
        });
      }
    });

    // 3. Any uncategorized custom tabs remain standalone
    const remaining = tabs.filter((t) => !claimedTabIds.has(t.id));

    return { topTabs: top, groupedSections: grouped, otherTabs: remaining };
  }, [tabs]);

  const renderSingleTab = (tab) => {
    const isActive = activeTab === tab.id;
    const IconComponent = TAB_ICONS[tab.icon] || Building2;

    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => handleSelect(tab.id)}
        className={`${styles.navItem} ${
          isActive ? styles.navItemActive : ""
        } ${isCollapsed ? styles.navItemCollapsed : ""}`}
        title={isCollapsed ? tab.label : undefined}
      >
        <IconComponent className={styles.itemIcon} />
        {!isCollapsed && (
          <>
            <span className={styles.itemLabel}>{tab.label}</span>
            {tab.badge && Number(tab.badge) > 0 ? (
              <span className={styles.externalBadge}>{tab.badge}</span>
            ) : null}
          </>
        )}
      </button>
    );
  };

  return (
    <nav className={styles.navContainer} aria-label="Property Navigation">
      {/* Back Button */}
      <button
        type="button"
        onClick={handleBack}
        className={`${styles.navItem} ${styles.backButton} ${
          isCollapsed ? styles.navItemCollapsed : ""
        }`}
        title={isCollapsed ? "Back to All Properties" : undefined}
      >
        <ArrowLeft className={styles.itemIcon} />
        {!isCollapsed && (
          <span className={styles.itemLabel}>Back to Properties</span>
        )}
      </button>

      {/* Property Context Header Badge */}
      {!isCollapsed ? (
        <div className={styles.propertyHeaderCard}>
          <div className={styles.propertyName} title={property.society_name}>
            {property.society_name}
          </div>
          <div className={styles.propertyMetaRow}>
            <span className={styles.propertyTypeTag}>
              {property.society_type || "PROPERTY"}
            </span>
            <span className={styles.propertyIdTag}>ID: {property.society_id}</span>
          </div>
        </div>
      ) : (
        <div className={styles.sectionHeaderCollapsed} />
      )}

      {/* Property Module Section Header */}
      <SidebarSection title="Property Modules" isCollapsed={isCollapsed} />

      {/* Pinned Primary Overview Tab (e.g. Buildings & Depts) */}
      {topTabs.map(renderSingleTab)}

      {/* Categorized Collapsible Dropdowns or Single Items */}
      {groupedSections.map((group) => {
        // If a group only contains 1 tab, render directly as a standalone item
        if (group.tabs.length === 1) {
          return renderSingleTab(group.tabs[0]);
        }
        // If group contains multiple items, render clean collapsible dropdown
        return (
          <PropertyNavDropdown
            key={group.groupId}
            group={group}
            activeTab={activeTab}
            isCollapsed={isCollapsed}
            onSelectTab={handleSelect}
          />
        );
      })}

      {/* Any remaining ungrouped custom tabs */}
      {otherTabs.map(renderSingleTab)}
    </nav>
  );
};

export default SidebarPropertyNav;
